import React, {SVGProps, useEffect, useRef, useState} from 'react';
import {drawArcPath} from '@di-shared/utils/drawArcPath';

interface HalfDonutChartProps {
  color: string;
  ratio: number;
  width: number;
  strokeWidth: number;
  text?: Omit<SVGProps<SVGTextElement>, 'ref' | 'x' | 'y' | 'alignmentBaseline' | 'textAnchor'> & {
    offset: number;
    left: string;
    right: string;
  };
}

enum TextBoxPosition {
  LEFT,
  RIGHT,
}

function getRadius(width: number, maxTextWidth: number, strokeWidth: number) {
  const isStrokeThickerThanText = strokeWidth > maxTextWidth;
  return isStrokeThickerThanText ? (width - strokeWidth) / 2 : (width - maxTextWidth) / 2;
}

function getHeight(radius: number, strokeWidth: number, offset: number, maxTextHeight: number) {
  return maxTextHeight + offset + radius + strokeWidth;
}

function getCenterX(width: number) {
  return width / 2;
}

function getCenterY(radius: number, strokeWidth: number) {
  return strokeWidth / 2 + radius;
}

function getTextBoxCenterX(width: number, strokeWidth: number, maxTextWidth: number, textBoxPosition: TextBoxPosition) {
  const isStrokeThickerThanText = strokeWidth > maxTextWidth;
  // left
  if (textBoxPosition === TextBoxPosition.LEFT) {
    return isStrokeThickerThanText ? strokeWidth / 2 : maxTextWidth / 2;
  }
  // right
  return isStrokeThickerThanText ? width - strokeWidth / 2 : width - maxTextWidth / 2;
}

function getTextBoxCenterY(height: number, maxTextHeight: number) {
  return height - maxTextHeight / 2;
}

function drawArcPercentage(ratio: number, radius: number, centerX: number, centerY: number) {
  const startAngle = 180;
  const endAngle = 180 + 180 * ratio;
  return drawArcPath(startAngle, endAngle, radius, centerX, centerY);
}

interface RefObject {
  current: SVGTextElement | null;
}

interface MaxDimensions {
  maxTextWidth: number;
  maxTextHeight: number;
}

function getMaxDimensions(refs: RefObject[]): MaxDimensions {
  return refs.reduce(
    (acc: MaxDimensions, ref: RefObject) => {
      if (ref.current) {
        const {width, height} = ref.current.getBBox();
        return {
          maxTextWidth: Math.max(acc.maxTextWidth, width),
          maxTextHeight: Math.max(acc.maxTextHeight, height),
        };
      }
      return acc;
    },
    {maxTextWidth: 0, maxTextHeight: 0}
  );
}

function HalfDonutChart({color, ratio, width, strokeWidth, text}: HalfDonutChartProps) {
  const [height, setHeight] = useState<number>(width / 2 + strokeWidth / 2);
  const [radius, setRadius] = useState<number>(width / 2 - strokeWidth / 2);
  const [centerX, setCenterX] = useState<number>(width / 2);
  const [centerY, setCenterY] = useState<number>(radius + strokeWidth / 2);

  const leftTextRef = useRef<SVGTextElement>(null);
  const rightTextRef = useRef<SVGTextElement>(null);

  const [textWidth, setTextWidth] = useState<number>(0);
  const [textHeight, setTextHeight] = useState<number>(0);

  useEffect(() => {
    // text box 크기에 영향을 미치는 요소: 폰트 사이즈, 텍스트, 폰트 -> 렌더링 후 계산
    // left text와 right 텍스트중 큰 것으로 계산
    // 내부 요소의 크기가 text box 크기에 종속되어있어 useEffect 사용

    const maxDimensions = getMaxDimensions([leftTextRef, rightTextRef]);
    const {maxTextWidth, maxTextHeight} = maxDimensions;
    setTextWidth(maxTextWidth);
    setTextHeight(maxTextHeight);

    const radius = getRadius(width, maxTextWidth, strokeWidth);
    setRadius(radius);
    setHeight(getHeight(radius, strokeWidth, text?.offset || 0, maxTextHeight));
    setCenterX(getCenterX(width));
    setCenterY(getCenterY(radius, strokeWidth));
  }, []);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {text?.left && (
        <text
          ref={leftTextRef}
          x={getTextBoxCenterX(width, strokeWidth, textWidth, TextBoxPosition.LEFT)}
          y={getTextBoxCenterY(height, textHeight)}
          alignmentBaseline="central"
          textAnchor="middle"
          fill={'var(--on-surface-container-mid)'}
          {...text}
        >
          {text.left}
        </text>
      )}
      {text?.right && (
        <text
          ref={rightTextRef}
          x={getTextBoxCenterX(width, strokeWidth, textWidth, TextBoxPosition.RIGHT)}
          y={getTextBoxCenterY(height, textHeight)}
          alignmentBaseline="central"
          textAnchor="middle"
          fill={'var(--on-surface-container-mid)'}
          {...text}
        >
          {text.right}
        </text>
      )}
      <path
        d={drawArcPercentage(1, radius, centerX, centerY)}
        stroke="var(--surface-container-low)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      {!!ratio && (
        <path
          d={drawArcPercentage(ratio, radius, centerX, centerY)}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export default HalfDonutChart;
